import { Fragment, useEffect, useMemo, useState } from "react";
import { ScaledForm } from "./pn/PnForm.jsx";
import { parsePnXml } from "../../../lib/pn/parse.js";
import { pnFormHtml } from "../../../lib/pn/form.js";
import formCssUrl from "../../../lib/pn/form.css?url";
import { api, money, day } from "../api.js";
import { DOC_TYPES } from "../../../lib/primary/model.js";
import { SectionHead, PartialErrors, shortName, Pager } from "./common.jsx";
import { useResource } from "./useResource.js";
import PnRegistrations from "./pn/PnRegistrations.jsx";
import PnReceivedRk from "./pn/PnReceivedRk.jsx";
import Money from "../components/Money.jsx";

// ПН/РК з ЄРПН: видані й отримані, позиції, оригінал p7s
export default function Erpn({ companyId, company, sub, res, version, onSync }) {
  const canEdit = ["preparer", "submitter", "owner"].includes(company.role);
  return (
    <>
      <SectionHead title="ПН/РК (ЄРПН)" res={res} onSync={onSync}>
        {canEdit && <a className="btn btn-sm" href={`#/c/${companyId}/pn`}>Створити накладну</a>}
      </SectionHead>
      <PartialErrors res={res} />
      <Invoices key={sub || ""} companyId={companyId} company={company} sub={sub} reloadKey={version} />
    </>
  );
}

// sub з пошуку в шапці: «out/<номер>» або «in/<номер>» — відкриває список з цим запитом
const GROUPS = [["registered", "Зареєстровано", "ok"], ["commission", "За рішенням Комісії", "ok"], ["stopped", "Зупинено", "bad"], ["other", "Інше", "idle"]];
const TONE = Object.fromEntries(GROUPS.map(([k, , t]) => [k, t]));
const SIZE = 10;
// Останні 24 місяці для фільтра: «Жовтень 2026»
const MONTHS = Array.from({ length: 24 }, (_, i) => {
  const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - i);
  const l = d.toLocaleDateString("uk-UA", { month: "long", year: "numeric" }).replace(" р.", "");
  return [`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, l[0].toUpperCase() + l.slice(1)];
});
function monthRange(ym) {
  if (!ym) return {};
  const [y, m] = ym.split("-").map(Number);
  return { from: `${ym}-01`, to: `${ym}-${String(new Date(y, m, 0).getDate()).padStart(2, "0")}` };
}

function Invoices({ companyId, company, sub, reloadKey }) {
  const m = String(sub || "").match(/^(in|out)\/(.+)$/);
  const [direction, setDirection] = useState(m ? m[1] : ["reg", "rk"].includes(sub) ? sub : "out");
  const [q, setQ] = useState(m ? m[2] : "");
  const [month, setMonth] = useState("");
  const [group, setGroup] = useState("");
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [byParty, setByParty] = useState(false);
  const reset = () => { setPage(0); setOpen(null); };
  useEffect(() => {
    if (direction === "reg" || direction === "rk") return;
    const params = new URLSearchParams({ direction, page: String(page), size: String(SIZE), sort: byParty ? "party" : "impdate", ...monthRange(month) });
    if (q.trim()) params.set("q", q.trim());
    if (group) params.set("group", group);
    const t = setTimeout(() => api(`/api/companies/${companyId}/invoices?${params}`).then(setData), 200);
    return () => clearTimeout(t);
  }, [companyId, direction, q, page, month, group, byParty, reloadKey]);
  // Вибрана ПН — під списком; без вибору — перша на сторінці
  const selected = data?.invoices?.find((i) => i.code === open) || data?.invoices?.[0] || null;
  const pages = data ? Math.ceil(data.total / data.pageSize) : 0;
  const counts = data?.groups || {};
  const all = Object.values(counts).reduce((s, g) => s + g.n, 0);
  const party = direction === "out" ? "Покупець" : "Продавець";
  return (
    <div className="card">
      <div className="row">
        <div className="tabs" role="group" aria-label="Розділ">
          {[["out", "Видані"], ["in", "Отримані"], ["reg", "Реєстрація"], ["rk", "Отримані РК"]].map(([d, label]) => (
            <button type="button" key={d} className={direction === d ? "tab active" : "tab"} aria-pressed={direction === d}
              onClick={() => { setDirection(d); setGroup(""); reset(); }}>{label}</button>
          ))}
        </div>
      </div>
      {direction === "reg" ? <PnRegistrations companyId={companyId} reloadKey={reloadKey} /> : direction === "rk" ? <PnReceivedRk companyId={companyId} company={company} /> : (
        <>
          <div className="erpn-filters">
            <select className="erpn-month" value={month} onChange={(e) => { setMonth(e.target.value); reset(); }} aria-label="Місяць складання ПН">
              <option value="">Усі місяці</option>
              {MONTHS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <input className="search" type="search" aria-label="Пошук" placeholder="Номер, контрагент, код" value={q} onChange={(e) => { setQ(e.target.value); reset(); }} />
            <label className="check erpn-group"><input type="checkbox" checked={byParty} onChange={(e) => { setByParty(e.target.checked); reset(); }} />Групувати за контрагентом</label>
          </div>
          <div className="erpn-chips" role="group" aria-label="Стан реєстрації">
            <button type="button" className={`chip-btn${!group ? " on" : ""}`} aria-pressed={!group} onClick={() => { setGroup(""); reset(); }}>Усі <b>{all}</b></button>
            {GROUPS.filter(([k]) => counts[k]).map(([k, label, tone]) => (
              <button type="button" key={k} className={`chip-btn tone-${tone}${group === k ? " on" : ""}`} aria-pressed={group === k} onClick={() => { setGroup(group === k ? "" : k); reset(); }}>
                {label} <b>{counts[k].n}</b><span className="muted small"> · ПДВ {money(counts[k].vat)}</span></button>
            ))}
          </div>
          {!data ? <p className="muted">Завантаження…</p> : data.total === 0 ? <p className="muted">{all ? "За цими умовами документів немає." : "Документів немає. Запустіть синк ЄРПН."}</p> : (
            <>
              <div className="table-wrap">
                <table className="erpn-table">
                  <thead><tr><th>Зареєстровано</th><th>Дата ПН</th><th>№</th><th>{party}</th><th className="num">ПДВ</th><th className="num">Сума</th><th>Стан</th></tr></thead>
                  <tbody>
                    {data.invoices.map((i, n) => {
                      const name = direction === "out" ? i.buyer : i.seller;
                      const prev = data.invoices[n - 1], prevName = prev && (direction === "out" ? prev.buyer : prev.seller);
                      return (
                        <Fragment key={i.code}>
                          {byParty && name !== prevName && <tr className="erpn-grouprow"><td colSpan={7}>{name} <span className="muted small">код {i.cp_tin}</span></td></tr>}
                          <tr tabIndex={0} aria-selected={selected?.code === i.code} className={selected?.code === i.code ? "selected" : ""}
                            onClick={() => setOpen(i.code)}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(i.code); } }}>
                            <td>{day(i.impdate)}</td><td>{day(i.crtdate)}</td>
                            <td>{i.nmr}{i.doc_kind === "РК" && <span className="tag">РК</span>}</td>
                            <td className="erpn-party" title={name}>{shortName(name)}</td>
                            <td className="num"><Money value={i.vat_sum} /></td><td className="num"><Money value={i.total} /></td>
                            <td><span className={`badge badge-${TONE[i.status_group] || "idle"}`}>{statusShort(i.status_name)}</span></td>
                          </tr>
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {pages > 1 && <Pager page={page} pages={pages} total={data.total} go={(n) => { setPage(n); setOpen(null); }} />}
              {selected && <InvoicePanel key={selected.code} companyId={companyId} inv={selected} direction={direction} canEdit={["preparer", "submitter", "owner"].includes(company.role)} />}
            </>
          )}
        </>
      )}
    </div>
  );
}

// Вибрана ПН під списком: бланк затвердженої форми з p7s ЄРПН, офіційний PDF і квитанція ДПС, позиції
export function InvoicePanel({ companyId, inv, direction, canEdit = false }) {
  const [xml, setXml] = useState(null), [err, setErr] = useState(null);
  useEffect(() => { api(`/api/companies/${companyId}/invoices/${inv.code}/xml`).then((r) => setXml(r.xml), setErr); }, [companyId, inv.code]);
  const html = useMemo(() => (xml ? pnFormHtml(parsePnXml(xml), { cssHref: new URL(formCssUrl, location.href).href }) : ""), [xml]);
  return (
    <section className="erpn-panel" aria-label={`${inv.doc_kind || "ПН"} № ${inv.nmr}`}>
      <div className="row">
        <div><h3>{inv.doc_kind || "ПН"} № {inv.nmr} від {day(inv.crtdate)}</h3>
          <p className="muted small">{shortName(direction === "out" ? inv.buyer : inv.seller)} · зареєстровано {day(inv.impdate)} · {statusShort(inv.status_name)}</p>
          <PrimaryOf companyId={companyId} code={inv.code} />
          {canEdit && (inv.doc_kind || "ПН") === "ПН" && <a className="btn btn-secondary btn-sm" href={`#/c/${companyId}/pn?from=${inv.code}`}>
            {direction === "in" ? "Перепродати з націнкою" : "Нова ПН на основі цієї"}</a>}</div>
        <ErpnFiles companyId={companyId} code={inv.code} />
      </div>
      {err ? <p className="error">{err.message}</p> : !xml ? <p className="muted" role="status">Завантаження накладної…</p> : <ScaledForm html={html} />}
      <details className="erpn-lines"><summary>Позиції</summary><InvoiceLines companyId={companyId} code={inv.code} /></details>
    </section>
  );
}

// До яких договорів і первинних документів прив'язана ПН/РК («Первинні документи» → договір → «Податкові накладні»)
function PrimaryOf({ companyId, code }) {
  const [d, setD] = useState(null);
  useEffect(() => { api(`/api/companies/${companyId}/primary/by-invoice/${code}`).then(setD, () => setD({ documents: [] })); }, [companyId, code]);
  if (!d) return null;
  if (!d.documents.length) return <p className="muted small">Договір: не прив'язано</p>;
  return (
    <p className="small">Договір: {d.documents.map((x, i) => (
      <span key={x.id}>{i ? "; " : ""}{x.contract && <a href={`#/c/${companyId}/primary/op/${x.contract.operationId}`}>{x.contract.number ? `№ ${x.contract.number}${x.contract.date ? ` від ${day(x.contract.date)}` : ""}` : "без договору"}</a>}
        {" · "}<a href={`#/c/${companyId}/primary/doc/${x.id}`}>{DOC_TYPES[x.type]?.short || "Документ"}{x.number ? ` № ${x.number}` : ""}</a></span>))}</p>
  );
}

// «зареєстровано за рiшенням Комiсiї» → «за рішенням Комісії»; латинська «i» з ДПС → українська
const statusShort = (s) => {
  const t = String(s || "").replace(/i/g, "і");
  if (/коміс/i.test(t)) return "за рішенням Комісії";
  if (/зупин/i.test(t)) return "зупинено";
  return t || "—";
};

function InvoiceLines({ companyId, code }) {
  const [d, setD] = useState(null);
  useEffect(() => { api(`/api/companies/${companyId}/invoices/${code}`).then(setD); }, [companyId, code]);
  if (!d) return <p className="muted">Завантаження…</p>;
  return (
    <div className="lines">
      <div className="row">
        <b>{d.lines.length} позиц.</b>
      </div>
      <div className="table-wrap">
        <table>
          <thead><tr><th>#</th><th>Найменування</th><th>УКТЗЕД</th><th>Од.</th><th className="num">К-сть</th><th className="num">Ціна</th><th className="num">ПДВ, %</th></tr></thead>
          <tbody>
            {d.lines.map((l) => (
              <tr key={l.rnum}><td>{l.rnum}</td><td>{l.name}</td><td>{l.uktzed}</td><td>{l.unit}</td>
                <td className="num">{l.qty}</td><td className="num">{money(l.price)}</td><td className="num">{l.vat_rate}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Файли документа з ЄРПН: PDF накладної й квитанції (як у кабінеті), p7s. Переглянути — у новій вкладці
// (сайт не дозволяє вбудовувати сторінки в рамки), завантажити — файлом
function ErpnFiles({ companyId, code }) {
  const [busy, setBusy] = useState(null), [err, setErr] = useState(null);
  const base = `/api/companies/${companyId}/invoices/${code}/file`;
  // Спершу отримуємо файл (сервер збереже його), потім: перегляд — вкладка з адресою файла (вбудований
  // переглядач PDF браузера), завантаження — файлом. Вкладку відкриваємо одразу, інакше браузер її заблокує
  const get = async (kind, view) => {
    setBusy(`${kind}${view ? "v" : ""}`); setErr(null);
    const win = view ? window.open("", "_blank") : null;
    try {
      const r = await fetch(`${base}/${kind}`, { credentials: "same-origin" });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `Помилка ${r.status}`);
      if (view) { win.location.href = `${base}/${kind}?inline=1`; return; }
      const url = URL.createObjectURL(await r.blob());
      const name = (r.headers.get("content-disposition") || "").match(/filename="([^"]+)"/)?.[1] || `${code}-${kind}.pdf`;
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      document.body.append(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { win?.close(); setErr(e.message); } finally { setBusy(null); }
  };
  const rc = useResource(`/api/companies/${companyId}/invoices/${code}/receipts`);
  return (
    <div className="erpn-files">
      <span className="erpn-files-group" role="group" aria-label="Накладна, PDF">
        <button type="button" className="btn btn-secondary btn-sm" disabled={Boolean(busy)} onClick={() => get("pdf", true)}>{busy === "pdfv" ? "…" : "Переглянути PDF"}</button>
        <button type="button" className="btn btn-ghost btn-sm" disabled={Boolean(busy)} onClick={() => get("pdf", false)} aria-label="Завантажити накладну в PDF">⬇ PDF</button>
      </span>
      {rc.pending ? <span className="muted small">квитанції…</span> : rc.error ? <span className="error small">{rc.error.message}</span> :
        !rc.data.receipts.length ? <span className="muted small">квитанцій ще немає</span> :
        rc.data.receipts.map((k) => (
          <span key={k.kind} className="erpn-files-group" role="group" aria-label={`Квитанція ${k.n}`}>
            <button type="button" className="btn btn-secondary btn-sm" disabled={Boolean(busy)} onClick={() => get(k.kind, true)} title={k.title}>
              {busy === `${k.kind}v` ? "…" : `Квитанція ${k.n}`}<span className="muted small"> · {k.title[0].toLowerCase() + k.title.slice(1)}</span></button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={Boolean(busy)} onClick={() => get(k.kind, false)} aria-label={`Завантажити квитанцію ${k.n} в PDF`}>⬇ PDF</button>
          </span>
        ))}
      <a className="btn btn-ghost btn-sm" href={`/api/companies/${companyId}/invoices/${code}/p7s`}>p7s</a>
      {err && <span className="error small" role="alert">{err}</span>}
    </div>
  );
}
